import { startOfDay, subDays } from "date-fns";
import { useMemo } from "react";
import { useRecentTimelinePreviewQuery } from "web_myplann/Api/NotificationCenter/useRecentTimelinePreviewQuery";
import {
	useNotificationCenterRightOptional,
	useRemindersRightOptional,
} from "web_myplann/Api/Rights/RightsProvider";
import {
	DASHBOARD_PAST_WINDOW_DAYS,
	DASHBOARD_PREVIEW_LIMIT,
} from "web_myplann/Dashboard/constants";
import { useIsDashboardV2 } from "web_myplann/Dashboard/State/dashboardVariantContext";
import {
	type TimelineItemResponseDTO as TimelineItem,
	TimelineItemCategoryEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { formatApiDate } from "web_myplann/Shared/Utils/apiDate";

const BASE_PREVIEW_CATEGORIES: TimelineItemCategoryEnum[] = [
	TimelineItemCategoryEnum.Activity,
	TimelineItemCategoryEnum.Contract,
	TimelineItemCategoryEnum.ClientAction,
];

interface RecentPreview {
	items: TimelineItem[];
	hasMore: boolean;
	isLoading: boolean;
}

export const useDashboardRecentPreview = (): RecentPreview => {
	const isV2 = useIsDashboardV2();
	const hasNotificationCenterRight = useNotificationCenterRightOptional();
	const hasRemindersRight = useRemindersRightOptional();

	const categories = useMemo(
		() => [
			...BASE_PREVIEW_CATEGORIES,
			...(hasNotificationCenterRight && hasRemindersRight
				? [TimelineItemCategoryEnum.Reminder]
				: []),
		],
		[hasNotificationCenterRight, hasRemindersRight],
	);

	const today = startOfDay(new Date());
	const dateFrom = formatApiDate(subDays(today, DASHBOARD_PAST_WINDOW_DAYS));
	const dateTo = formatApiDate(today);

	// BE applies the category + date-window filter and returns items already
	// sorted (newest first), so no client-side filtering/sorting is needed here.
	const { data, isLoading } = useRecentTimelinePreviewQuery({
		categories,
		dateFrom,
		dateTo,
		onlyUnsolved: isV2 || undefined,
		limit: DASHBOARD_PREVIEW_LIMIT,
	});

	return {
		items: data?.items ?? [],
		hasMore: data?.meta.hasMore ?? false,
		isLoading,
	};
};
